// Globals
import { memo, useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';

// Models
import { IAddress } from '@/models/addresses.model';

// Components
import { Select, TSelectOption } from '@/ui';

// Helpers
import { useBoolean } from '@/hooks';
import { AddressesAPI } from '@/api';

type TPlaceAutocompleteProps = {
  onChange?: (place: IAddress | string | null) => void;
  value?: string | IAddress;
  placeholder?: string;
  disabled?: boolean;
};

export const PlaceAutocomplete = ({ onChange, value, disabled, placeholder = '' }: TPlaceAutocompleteProps) => {
  const { t } = useTranslation('common');

  const { value: isLoading, setTrue: setLoading, setFalse: resetLoading } = useBoolean();

  const [options, setOptions] = useState<TSelectOption<string>[]>([]);

  // Debounced function to fetch suggestions
  const debouncedFetchSuggestions = useCallback(
    debounce(async (query: string) => {
      if (query.trim().length >= 3) {
        try {
          setLoading();
          const response = await AddressesAPI.getAddressSuggestions({
            query: query.trim().replace(',', ' '),
            limit: 5,
          });

          const selectOptions =
            response.data?.map((item: IAddress) => ({
              label: item.full_address,
              value: item.place_id ?? item.full_address,
            })) || [];

          setOptions(selectOptions);
        } catch (error) {
          console.error('Failed to fetch address suggestions:', error);
          setOptions([]);
        } finally {
          resetLoading();
        }
      } else {
        setOptions([]);
      }
    }, 300),
    [],
  );

  // Handle search
  const handleSearch = useCallback(
    (searchValue: string) => {
      debouncedFetchSuggestions(searchValue);
    },
    [debouncedFetchSuggestions],
  );

  // Handle selection
  const handleSelect = useCallback(
    (selectedValue: unknown, option?: TSelectOption<unknown> | TSelectOption<unknown>[]) => {
      if (option && !Array.isArray(option)) {
        const address: IAddress = {
          full_address: option.label as string,
          place_id: option.value as string,
        };
        onChange?.(address);
      }
    },
    [onChange],
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedFetchSuggestions.cancel();
    };
  }, [debouncedFetchSuggestions]);

  const currentValue = typeof value === 'string' ? value : value?.full_address ?? undefined;

  return (
    <Select
      placeholder={placeholder}
      disabled={disabled}
      value={currentValue}
      options={options}
      onSearch={handleSearch}
      onChange={handleSelect}
      loading={isLoading}
      showSearch
      filterOption={false}
      suffixIcon={null}
      notFoundContent={t(isLoading ? 'addresses_loading' : 'addresses_not_found')}
    />
  );
};

export default memo(PlaceAutocomplete);
